import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Form, Button, Spinner, Alert, Card, Badge } from 'react-bootstrap';
import Message from '../../../Components/Message';
import apiClient from '../../../AppClient';
import PaymentModal from './PaymentModal';
import './CustomerPaymentsDashboard.css';
import { CURRENCY } from '../../../constants/Constants';

const CustomerPaymentsDashboard = () => {
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [filteredInstallments, setFilteredInstallments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [installments, setInstallments] = useState([]);
  const [viewMode, setViewMode] = useState('payments'); // 'payments' or 'installments'
  const [customers, setCustomers] = useState([]);
  const [isLoadingInstallments, setIsLoadingInstallments] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (viewMode === 'payments') {
      filterTransactions();
    } else {
      filterInstallments();
    }
  }, [searchTerm, transactions, installments, viewMode]);

  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      await Promise.all([
        loadTransactions(),
        fetchCustomers(),
        loadInstallments()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
      setError('حدث خطأ أثناء تحميل البيانات الأولية');
    } finally {
      setIsLoading(false);
    }
  };


  const fetchCustomers = async () => {
    try {
      const response = await apiClient.get('/customers/');
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      // Fallback to mock data if needed
      setCustomers([
        { id: 1, name: 'أحمد خالد' },
        { id: 2, name: 'محمد علي' },
        { id: 3, name: 'سارة أحمد' }
      ]);
    }
  };

  const loadTransactions = async () => {
    try {
      const response = await apiClient.get('/transactions/customerpayments/');
      const data = response.data;
      setTransactions(Array.isArray(data) ? data : [data]);
    } catch (error) {
      console.error('خطأ في تحميل المعاملات:', error);
      // Mock data as fallback
      setTransactions([
        {
          id: 3,
          customer: {
            id: 3,
            customer_type: "2س2",
            name: "romani",
            phone: "010999fgd199",
            remaining_amount: "4909.99"
          },
          transaction_date: "2025-02-28",
          amount: "30.00",
          remaining_balance: "4910.00",
          payment_method: "VISA",
          notes: "k"
        },
      ]);
    }
  };

  const loadInstallments = async (customer = null) => {
    setIsLoadingInstallments(true);
    try {
      console.log('Loading installments for customer:', customer);
      const response = await apiClient.get(`/transactions/installments/?customer=${customer || ''}`);

      // Handle the correct response format based on the JSON structure you provided
      if (response.data && Array.isArray(response.data.installments)) {
        setInstallments(response.data.installments);
      } else if (Array.isArray(response.data)) {
        // Fallback for direct array response
        setInstallments(response.data);
      } else {
        console.warn('Unexpected response format:', response.data);
        setInstallments([]);
      }

      setError(null);
      return true;
    } catch (error) {
      console.error('خطأ في تحميل الأقساط:', error);

      if (error.response) {
        // Handle specific HTTP errors
        if (error.response.status === 404) {
          setError('الرابط غير موجود. يرجى التحقق من صحة المسار.');
        } else {
          setError(`خطأ في تحميل الأقساط (${error.response.status}). يرجى المحاولة مرة أخرى.`);
        }
      } else if (error.request) {
        // No response received
        setError('لم يتم تلقي استجابة من الخادم. يرجى التحقق من اتصالك بالإنترنت.');
      } else {
        // Setup or request error
        setError('خطأ في تحميل الأقساط. يرجى المحاولة مرة أخرى.');
      }

      setInstallments([]);
      return false;
    } finally {
      setIsLoadingInstallments(false);
    }
  };

  const filterTransactions = () => {
    if (!searchTerm.trim()) {
      setFilteredTransactions(transactions);
      return;
    }

    const filtered = transactions.filter(transaction => {
      const customerName = transaction.customer?.name?.toLowerCase() || '';
      const paymentMethod = transaction.payment_method?.toLowerCase() || '';
      const searchLower = searchTerm.toLowerCase();

      return (
        customerName.includes(searchLower) ||
        paymentMethod.includes(searchLower)
      );
    });

    setFilteredTransactions(filtered);
  };

  const filterInstallments = () => {
    if (!searchTerm.trim()) {
      setFilteredInstallments(installments);
      return;
    }

    const filtered = installments.filter(installment => {
      const customerName = installment.customer?.toLowerCase() || '';
      const notes = installment.notes?.toLowerCase() || '';
      const searchLower = searchTerm.toLowerCase();

      return (
        customerName.includes(searchLower) ||
        notes.includes(searchLower)
      );
    });

    setFilteredInstallments(filtered);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const addPayment = async (paymentData) => {
    try {
      // If installment_id exists, we need to mark the installment as paid
      if (paymentData.installment_id) {
        // First make the payment
        const paymentResponse = await apiClient.post('/transactions/customerpayments/add/', {
          customer: parseInt(paymentData.customer, 10),
          installment: parseInt(paymentData.installment_id, 10),
          payment_method: paymentData.payment_method,
          amount: parseFloat(paymentData.amount),
          notes: paymentData.notes
        });

      } else {
        // Normal payment without installment
        await apiClient.post('/transactions/customerpayments/add/', {
          customer: parseInt(paymentData.customer, 10),
          payment_method: paymentData.payment_method,
          amount: parseFloat(paymentData.amount),
          notes: paymentData.notes
        });
      }

      alert('تمت الإضافة بنجاح!');
      await loadTransactions();
      await loadInstallments();
      setShowAddModal(false);
      setError(null);
      return true; // Return true to indicate success
    } catch (error) {
      console.error('Error adding payment:', error);
      // Now we throw the error back to the modal instead of handling it here
      throw error;
    }
  };

  const handleSubmit = async (formData) => {
    // Process form data from the modal
    try {
      await addPayment(formData);
      return true;
    } catch (error) {
      throw error; // Re-throw to be caught by the modal
    }
  };

  const refreshData = async () => {
    setError(null);
    try {
      await Promise.all([
        loadTransactions(),
        loadInstallments( selectedCustomer || null )       
      ]);
    } catch (error) {
      console.error("Error refreshing data:", error);
      setError('حدث خطأ أثناء تحديث البيانات');
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount).toFixed(2);
  };

  const handlePayInstallment = (installmentId) => {
    // Find the installment details
    const installment = installments.find(inst => inst.id === installmentId);
    if (!installment) return;

    // Find the customer ID from the name
    const customer = customers.find(c => c.name === installment.customer);
    if (!customer) return;

    // Open the payment modal with pre-filled data
    setShowAddModal(true);
    // Pre-fill the payment form with installment details
  };

  const handleCustomerChange = async (e) => {
    const customerId = e.target.value;
    setSelectedCustomer(customerId || null);
    

    if (customerId) {
      // Clear previous data while loading
      setInstallments([]);
      await loadInstallments(customerId );
    } else {
      // If no customer selected, load all installments
      await loadInstallments();
    }
  };

  // Loading state - full page spinner
  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center vh-100" dir="rtl" >
        <Spinner animation="border" variant="success">
          <span className="visually-hidden">جاري التحميل...</span>
        </Spinner>
      </Container>
    );
  }

  return (
    <Container fluid="md" className="py-4" dir="rtl">
      <Card className="shadow border-0 mb-4">
        <Card.Header className="text-white p-3 d-flex justify-content-between align-items-center" style={{ backgroundColor: "#d1e7dd " }}>
          <h2 className="h3 mb-0" style={{ color: "#0f5132" }}>لوحة مدفوعات العملاء</h2>
          <div className="btn-group">
            <Button
              variant={viewMode === 'payments' ? "success" : "outline-success"}
              onClick={() => setViewMode('payments')}
            >
              المدفوعات
            </Button>
            <Button
              variant={viewMode === 'installments' ? "success" : "outline-success"}
              onClick={() => setViewMode('installments')}
            >
              الأقساط
            </Button>
          </div>
        </Card.Header>

        <Card.Body>
          {/* Only show dashboard errors here, not form submission errors */}
          {error && <Alert variant="danger">{error}</Alert>}

          <Row className="mb-4 align-items-center">
            <Col md={4} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder={viewMode === 'payments' ? "بحث في المعاملات..." : "بحث في الأقساط..."}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </Form.Group>
            </Col>
            {viewMode === 'installments' && (
              <>
                <Col md={3}>
                  <Form.Select
                    value={selectedCustomer || ''}
                    onChange={handleCustomerChange}
                    disabled={isLoadingInstallments}
                  >
                    <option value="">اختر عميل</option>
                    {customers.map(customer => (
                      <option key={customer.id} value={customer.id}>
                        {customer.name}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={3}>
                </Col>
              </>
            )}
            <Col className="d-flex justify-content-end">
              {isLoadingInstallments && viewMode === 'installments' && (
                <Spinner animation="border" size="sm" className="me-2" />
              )}
              <Button
                style={{ marginLeft: '5px' }}
                variant="outline-secondary"
                className="me-2"
                onClick={refreshData}
                disabled={isLoadingInstallments}
              >
                🔄 تحديث
              </Button>
              <Button
                variant="success"
                onClick={() => setShowAddModal(true)}
              >
                <i className="fas fa-plus"></i> إضافة
              </Button>
            </Col>
          </Row>

          {isLoadingInstallments && viewMode === 'installments' ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="success" />
              <p className="mt-3">جاري تحميل الأقساط...</p>
            </div>
          ) : filteredTransactions.length === 0 && viewMode === 'payments' ? (
            <div className="text-center py-5">
              <div className="empty-state">
                <span className="display-4">📄</span>
                <p className="mt-3">لم يتم العثور على معاملات</p>
              </div>
            </div>
          ) : filteredInstallments.length === 0 && viewMode === 'installments' ? (
            <div className="text-center py-5">
              <div className="empty-state">
                <span className="display-4">📄</span>
                <p className="mt-3">لم يتم العثور على أقساط</p>
              </div>
            </div>
          ) : viewMode === 'payments' ? (
            <div className="table-responsive">
              <Table bordered hover>
                <thead className="bg-light">
                  <tr>
                    <th>العميل</th>
                    <th>المبلغ</th>
                    <th>الباقي</th>
                    <th>طريقة الدفع</th>
                    <th>تاريخ الدفع</th>
                    <th>ملاحظات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.map(transaction => (
                    <tr key={transaction.id}>
                      <td>{transaction.customer?.name || 'غير معروف'}</td>
                      <td>{formatCurrency(transaction.amount)} {CURRENCY}</td>
                      <td>{formatCurrency(transaction.remaining_balance)} {CURRENCY}</td>
                      <td>{transaction.payment_method}</td>
                      <td>{transaction.transaction_date}</td>
                      <td>{transaction.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <div className="table-responsive">
              <Table bordered hover>
                <thead className="bg-light">
                  <tr>
                    <th>العميل</th>
                    <th>قسط رقم</th>
                    <th>المبلغ</th>
                    <th>تاريخ الإنشاء</th>
                    <th>تاريخ الاستحقاق</th>
                    <th>الحالة</th>
                    <th>ملاحظات</th>
                    <th>الإجراءات</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInstallments.map(installment => (
                    <tr key={installment.id} className="">
                      <td>{installment?.customer || 'غير معروف'}</td>
                      <td>{installment.installment_number}</td>
                      <td>{formatCurrency(installment.amount)} {CURRENCY}</td>
                      <td>{new Date(installment.created_at).toLocaleDateString('ar-EG')}</td>
                      <td>{new Date(installment.due_date).toLocaleDateString('ar-EG')}</td>
                      <td>
                        {installment.is_paid ? (
                          <Badge bg="success">مدفوع - تم تسديد القسط بالكامل</Badge>
                        ) : installment.status === 'cancled' ? (
                          <Badge bg="secondary">ملغى - تم إلغاء القسط</Badge>
                        ) : new Date(installment.due_date) < new Date() ? (
                          <Badge bg="danger">متأخر - تجاوز تاريخ الاستحقاق</Badge>
                        ) : (
                          <Badge bg="warning">غير مدفوع - لم يتم تسديد القسط بعد</Badge>
                        )}
                      </td>
                      <td>{installment.notes}</td>
                      <td>
                        {!installment.is_paid && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handlePayInstallment(installment.id)}
                          >
                            سداد
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      <PaymentModal
        show={showAddModal}
        handleClose={() => setShowAddModal(false)}
        customers={customers}
        handleSubmit={handleSubmit}
        apiClient={apiClient}  // Pass apiClient as a prop
        mode="add"
        initialValues={{
          customer: '',
          installment: '',
          payment_method: 'كاش',
          amount: '',
          notes: ''
        }}
      />
    </Container>
  );
};

export default CustomerPaymentsDashboard;